import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Typography, Box, Divider, CircularProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import api from "../../services/api";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px 12px",
  },
  content: {
    padding: "0 20px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  modeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  modeCard: {
    border: "2px solid",
    borderRadius: 10,
    padding: "12px 14px",
    cursor: "pointer",
    transition: "all 0.15s",
    userSelect: "none",
  },
  modeTitle: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: 3,
  },
  modeDesc: {
    fontSize: 11.5,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: 5,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  },
  varField: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  varPrefix: {
    fontSize: 16,
    color: "#6366f1",
    fontFamily: "monospace",
    flexShrink: 0,
  },
  actions: {
    padding: "12px 20px",
    gap: 8,
  },
  noCredentials: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    color: "#9a3412",
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────
const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();

  const [credentials, setCredentials] = useState([]);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [form, setForm] = useState(initialForm());
  const isEdit = open === "edit";

  // Load credentials on open
  useEffect(() => {
    if (!open) return;
    setLoadingCreds(true);
    api.get("/ai-credentials")
      .then(({ data: creds }) => setCredentials(creds || []))
      .catch(() => {})
      .finally(() => setLoadingCreds(false));
  }, [open]);

  // Populate form on edit
  useEffect(() => {
    if (open === "edit") {
      const t = data?.data?.typebotIntegration || {};
      setForm({
        name: t.name || "",
        credentialId: t.credentialId ? String(t.credentialId) : "",
        flowMode: t.flowMode || "oneshot",
        prompt: t.prompt || "",
        outputVariable: t.outputVariable || "ai_response",
      });
    } else if (open === "create") {
      setForm(initialForm());
    }
  }, [open, data]);

  const set = (field) => (e) => {
    const value = e.target.value; // captura antes do event pool ser limpo pelo React
    setForm((f) => ({ ...f, [field]: value }));
  };
  const setMode = (mode) => setForm((f) => ({ ...f, flowMode: mode }));

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (!form.credentialId) return;
    if (!form.prompt.trim()) return;

    const cred = credentials.find((c) => String(c.id) === String(form.credentialId));

    const payload = {
      name: form.name.trim(),
      prompt: form.prompt.trim(),
      flowMode: form.flowMode,
      credentialId: Number(form.credentialId),
      apiKey: cred?.apiKey || "",
      model: cred?.model || "gpt-4o",
      outputVariable: form.flowMode === "oneshot" ? (form.outputVariable || "ai_response") : null,
      // Defaults transparentes (usuário não precisa ver)
      maxTokens: 800,
      temperature: 0.75,
      maxMessages: 10,
      contextMessages: 6,
      voice: "texto",
      provider: "openai",
      projectName: form.name.trim(),
    };

    if (isEdit) {
      onUpdate({ ...data, data: { typebotIntegration: payload } });
    } else {
      onSave({ typebotIntegration: payload });
    }
    close(null);
  };

  const canSave = form.name.trim() && form.credentialId && form.prompt.trim();

  return (
    <Dialog
      open={open === "create" || open === "edit"}
      onClose={() => close(null)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 12 } }}
    >
      {/* Header */}
      <div className={classes.title}>
        <Typography style={{ fontWeight: 700, fontSize: 16 }}>
          {isEdit ? "Editar bloco de IA" : "Configurar Inteligência Artificial"}
        </Typography>
        <IconButton size="small" onClick={() => close(null)}>
          <Close fontSize="small" />
        </IconButton>
      </div>
      <Divider />

      <DialogContent className={classes.content}>

        {/* Nome interno */}
        <div>
          <p className={classes.label}>Nome interno</p>
          <TextField
            value={form.name}
            onChange={set("name")}
            placeholder="Ex: Boas-vindas ao lead"
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ style: { fontSize: 13 } }}
          />
        </div>

        {/* Credencial */}
        <div>
          <p className={classes.label}>Conta de IA (credencial OpenAI)</p>
          {loadingCreds ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography style={{ fontSize: 12, color: "#94a3b8" }}>Carregando...</Typography>
            </Box>
          ) : credentials.length === 0 ? (
            <div className={classes.noCredentials}>
              ⚠️ Nenhuma credencial de IA cadastrada.{" "}
              <a href="/ai-credentials" target="_blank" style={{ color: "#c2410c", fontWeight: 600 }}>
                Criar credencial →
              </a>
            </div>
          ) : (
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={form.credentialId}
                onChange={set("credentialId")}
                displayEmpty
                style={{ fontSize: 13, borderRadius: 8 }}
              >
                <MenuItem value="" disabled style={{ fontSize: 13 }}>
                  Selecionar conta...
                </MenuItem>
                {credentials.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)} style={{ fontSize: 13 }}>
                    <Box>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 11 }}>
                        {c.model}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        {/* Modo */}
        <div>
          <p className={classes.label}>O que a IA deve fazer?</p>
          <div className={classes.modeGrid}>
            <ModeCard
              selected={form.flowMode === "oneshot"}
              onClick={() => setMode("oneshot")}
              emoji="⚡"
              title="Gerar texto"
              desc="Cria uma mensagem personalizada e o fluxo continua automaticamente. Ideal para boas-vindas e follow-ups."
              color="#6366f1"
            />
            <ModeCard
              selected={form.flowMode === "permanent"}
              onClick={() => setMode("permanent")}
              emoji="💬"
              title="Agente conversacional"
              desc="A IA assume a conversa e responde o cliente diretamente. Ideal para qualificação de leads e agendamentos."
              color="#0891b2"
            />
          </div>
        </div>

        {/* Prompt */}
        <div>
          <p className={classes.label}>Instruções para a IA</p>
          <TextField
            value={form.prompt}
            onChange={set("prompt")}
            multiline
            rows={5}
            variant="outlined"
            fullWidth
            placeholder={
              form.flowMode === "oneshot"
                ? "Descreva o que a IA deve escrever. Ex:\n\nVocê é um assistente de vendas. Escreva uma mensagem curta e cordial de boas-vindas para o lead {nome}, que acabou de entrar no CRM. Mencione o nome dele, agradeça o interesse e inclua um convite para conhecer melhor nosso produto."
                : "Descreva como a IA deve se comportar na conversa. Ex:\n\nVocê é um assistente de vendas da empresa. Seu objetivo é qualificar o lead, entendendo qual produto ele busca e seu orçamento. Seja cordial, objetivo e sempre tente agendar uma reunião ao final."
            }
            inputProps={{ style: { fontSize: 13, lineHeight: 1.6 } }}
          />
          <Typography style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            {form.flowMode === "oneshot"
              ? "Use {nome}, {email} ou {telefone} para inserir dados do contato na mensagem."
              : "A IA tem acesso ao histórico recente da conversa para manter contexto."}
          </Typography>
        </div>

        {/* Variável de saída — só no modo oneshot */}
        {form.flowMode === "oneshot" && (
          <div>
            <p className={classes.label}>Salvar resultado na variável</p>
            <div className={classes.varField}>
              <span className={classes.varPrefix}>{`{`}</span>
              <TextField
                value={form.outputVariable}
                onChange={set("outputVariable")}
                variant="standard"
                size="small"
                InputProps={{ disableUnderline: true, style: { fontSize: 13, fontFamily: "monospace" } }}
                style={{ flex: 1 }}
              />
              <span className={classes.varPrefix}>{`}`}</span>
            </div>
            <Typography style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              Use <code style={{ backgroundColor: "#f1f5f9", padding: "1px 5px", borderRadius: 3 }}>{`{${form.outputVariable || "ai_response"}}`}</code> em blocos de mensagem seguintes para inserir o texto gerado pela IA.
            </Typography>
          </div>
        )}

      </DialogContent>

      <Divider />
      <DialogActions className={classes.actions}>
        <Button onClick={() => close(null)} style={{ textTransform: "none", fontSize: 13 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!canSave}
          style={{ textTransform: "none", fontSize: 13, fontWeight: 600, borderRadius: 8 }}
        >
          {isEdit ? "Salvar alterações" : "Adicionar ao fluxo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Mode Card ────────────────────────────────────────────────────────────────
function ModeCard({ selected, onClick, emoji, title, desc, color }) {
  return (
    <div
      onClick={onClick}
      style={{
        border: `2px solid ${selected ? color : "#e2e8f0"}`,
        borderRadius: 10,
        padding: "12px 14px",
        cursor: "pointer",
        backgroundColor: selected ? `${color}08` : "#fff",
        transition: "all 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{emoji}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: selected ? color : "#374151" }}>
          {title}
        </span>
      </div>
      <p style={{ fontSize: 11.5, color: "#6b7280", margin: 0, lineHeight: 1.45 }}>{desc}</p>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function initialForm() {
  return {
    name: "",
    credentialId: "",
    flowMode: "oneshot",
    prompt: "",
    outputVariable: "ai_response",
  };
}

export default FlowBuilderOpenAIModal;
